import { hasPermission } from 'core/auth/rbac'
import { FilterBar } from 'core/components/ui/FilterBar'
import { Modal } from 'core/components/ui/Modal'
import { PageHeader } from 'core/components/ui/PageHeader'
import { useAuth } from 'features/auth/presentation/context/AuthContext'
import React, { useState } from 'react'
import { CatalogItem } from '../../domain/entities/Catalogue'
import { SkuFormModal } from '../components/SkuFormModal'
import { SkuTable } from '../components/SkuTable'
import { useCatalogue } from '../hooks/useCatalogue'

export const CataloguePage: React.FC = () => {
  const {
    items,
    loading,
    error,
    search,
    setSearch,
    statusFilter,
    setStatusFilter,
    page,
    setPage,
    totalPages,
    totalItems,
    createItem,
    updateItem,
    deleteItem,
    restoreItem,
    resetFilters,
  } = useCatalogue(10)

  const { user } = useAuth()
  const canEdit = hasPermission(user, 'edit_catalogue')

  const [formOpen, setFormOpen] = useState(false)
  const [selectedItem, setSelectedItem] = useState<CatalogItem | null>(null)

  // Soft delete confirmation modal state
  const [deleteConfirmItem, setDeleteConfirmItem] = useState<CatalogItem | null>(null)
  const [isDeleting, setIsDeleting] = useState(false)

  const handleAddClick = () => {
    setSelectedItem(null)
    setFormOpen(true)
  }

  const handleEditClick = (item: CatalogItem) => {
    setSelectedItem(item)
    setFormOpen(true)
  }

  const handleDeleteClick = (item: CatalogItem) => {
    setDeleteConfirmItem(item)
  }

  const handleRestoreClick = async (item: CatalogItem) => {
    await restoreItem(item.id)
  }

  const handleConfirmDelete = async () => {
    if (!deleteConfirmItem) return
    setIsDeleting(true)
    try {
      await deleteItem(deleteConfirmItem.id)
      setDeleteConfirmItem(null)
    } finally {
      setIsDeleting(false)
    }
  }

  const handleFormSubmit = async (
    data: Omit<CatalogItem, 'id' | 'addedBy' | 'isDeleted' | 'createdAt' | 'updatedAt'>,
  ) => {
    if (selectedItem) {
      await updateItem(selectedItem.id, data)
    } else {
      await createItem(data)
    }
  }

  const isFilterActive = !!search || statusFilter !== 'all'

  return (
    <div className="space-y-6">
      {/* Page Header */}
      <PageHeader
        title="Catalogue Management"
        description="Manage feed product formulas, packaging variant mappings, and MRP/selling price."
        breadcrumbs={[{ label: 'Portal', path: '/' }, { label: 'Catalogue' }]}
        actions={
          canEdit && (
            <button
              onClick={handleAddClick}
              className="px-4 py-2 text-sm font-semibold bg-primary hover:bg-primary-hover text-primary-foreground rounded-btn shadow transition-colors flex items-center gap-2"
            >
              <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2.5" d="M12 4v16m8-8H4" />
              </svg>
              Add New SKU
            </button>
          )
        }
      />

      {/* Fetch error banner */}
      {error && (
        <div className="p-3 text-sm bg-danger/10 border border-danger/20 text-danger rounded-btn font-medium">
          {error}
        </div>
      )}

      {/* Filter Bar */}
      <FilterBar
        searchPlaceholder="Search product name or description..."
        searchValue={search}
        onSearchChange={setSearch}
        showReset={isFilterActive}
        onReset={resetFilters}
      >
        <select
          value={statusFilter}
          onChange={(e) => {
            setStatusFilter(e.target.value as 'all' | 'active' | 'disabled')
            setPage(1)
          }}
          className="px-3 py-2 border border-border rounded-btn bg-surface text-text-primary text-xs font-semibold focus:outline-none"
        >
          <option value="all">All Statuses</option>
          <option value="active">Active Only</option>
          <option value="disabled">Disabled Only</option>
        </select>
      </FilterBar>

      {/* SKU Table Data Grid */}
      <SkuTable
        items={items}
        loading={loading}
        onEdit={handleEditClick}
        onDelete={handleDeleteClick}
        onRestore={handleRestoreClick}
        currentPage={page}
        totalPages={totalPages}
        onPageChange={setPage}
        totalItems={totalItems}
        itemsPerPage={10}
      />

      {/* Add / Edit Form Modal */}
      <SkuFormModal
        isOpen={formOpen}
        onClose={() => setFormOpen(false)}
        onSubmit={handleFormSubmit}
        item={selectedItem}
      />

      {/* Soft Delete / Disable Confirmation Modal */}
      <Modal
        isOpen={!!deleteConfirmItem}
        onClose={() => setDeleteConfirmItem(null)}
        title="Disable SKU"
        size="sm"
        footer={
          <>
            <button
              type="button"
              onClick={() => setDeleteConfirmItem(null)}
              disabled={isDeleting}
              className="px-4 py-2 text-sm font-semibold border border-border rounded-btn bg-surface hover:bg-bg text-text-secondary hover:text-text-primary transition-colors disabled:opacity-50"
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={handleConfirmDelete}
              disabled={isDeleting}
              className="px-4 py-2 text-sm font-semibold bg-danger hover:bg-danger/90 text-white rounded-btn shadow transition-colors disabled:opacity-50 flex items-center gap-2"
            >
              {isDeleting ? 'Disabling...' : 'Disable SKU'}
            </button>
          </>
        }
      >
        <div className="space-y-3">
          <p className="text-text-primary font-medium">
            Are you sure you want to disable <span className="font-bold text-danger">{deleteConfirmItem?.name}</span> (
            {deleteConfirmItem?.variant})?
          </p>
          <p className="text-xs text-text-secondary">
            This will mark the catalog item as disabled. It will remain in historical orders and stock records, but
            won&apos;t be available for new indents.
          </p>
        </div>
      </Modal>
    </div>
  )
}
